import React, { useEffect, useRef, useState } from 'react'
import ReactModal from 'react-modal'
import { getApiUrl } from 'utils/apiBaseUrl'
import Button from 'components/Button'
import CloseIcon from 'assets/icons/close-icon'
import { InputField, Label } from 'components/Input/style'
import {
  Actions,
  ErrorText,
  FieldLabel,
  FileName,
  FileRow,
  Hint,
  Intro,
  ModalFooter,
  ModalText,
  Page,
  ReportBox,
  ReportPre,
  TabBar,
  TabButton,
  TextArea,
  Title
} from './style'

const liveModalStyles = {
  content: {
    padding: '0',
    borderRadius: '8px',
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)'
  },
  overlay: {
    backgroundColor: 'rgba(32, 32, 32, 0.35)',
    zIndex: 1000
  }
}

const API = '/api/sync-factsheets'
const COUNTRY_API = '/api/sync-country-profiles'
const AUTH_KEY = 'factsheet-sync-secret'
const JOB_STATE_KEY = 'factsheet-sync-job-state'

function readStoredJob() {
  try {
    const raw = localStorage.getItem(JOB_STATE_KEY)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

function persistJob(next) {
  try {
    localStorage.setItem(JOB_STATE_KEY, JSON.stringify(next))
  } catch {
    // ignore
  }
}

function parseIds(text) {
  return text
    .split(/[\s,;]+/)
    .map((line) => line.trim())
    .filter((line) => line && !/^history[_ ]?id$/i.test(line))
}

function canResumeJob(job) {
  return Boolean(
    job
    && job.historyIds?.length
    && job.nextIndex < job.historyIds.length
    && (job.status === 'running' || job.status === 'cancelled' || job.status === 'failed')
  )
}

async function request(path, { method = 'GET', body, secret } = {}) {
  const response = await fetch(getApiUrl(path), {
    method,
    headers: {
      'Content-Type': 'application/json',
      'x-factsheet-sync-secret': secret
    },
    body: body ? JSON.stringify(body) : undefined
  })
  const json = await response.json().catch(() => ({}))
  if (!response.ok) {
    throw new Error(json.error || response.statusText || `Request failed (${response.status})`)
  }
  return json
}

const SyncFactsheetsPage = () => {
  const [secret, setSecret] = useState('')
  const [text, setText] = useState('')
  const [fileName, setFileName] = useState('')
  const [job, setJob] = useState(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [liveConfirmOpen, setLiveConfirmOpen] = useState(false)
  const [liveAction, setLiveAction] = useState('factsheets')
  const [tab, setTab] = useState('factsheets')
  const [countryJob, setCountryJob] = useState(null)
  const fileRef = useRef(null)
  const cancelledRef = useRef(false)

  useEffect(() => {
    try {
      const saved = sessionStorage.getItem(AUTH_KEY)
      if (saved) setSecret(saved)
    } catch {
      // ignore
    }
    const cached = readStoredJob()
    if (cached) setJob(cached)
  }, [])

  useEffect(() => {
    if (!busy) return undefined
    const onLeave = (event) => {
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', onLeave)
    return () => window.removeEventListener('beforeunload', onLeave)
  }, [busy])

  const ids = parseIds(text)
  const canPush = Boolean(ids.length && !busy)
  const resumeAvailable = !busy && canResumeJob(job)

  const rememberAuth = () => {
    sessionStorage.setItem(AUTH_KEY, secret)
  }

  const saveJob = (next) => {
    setJob(next)
    persistJob(next)
    return next
  }

  const runLoop = async (current, auth) => {
    cancelledRef.current = false
    setBusy(true)
    let next = current
    try {
      const batchSize = next.batchSize || 200
      while (next.nextIndex < next.historyIds.length) {
        if (cancelledRef.current) {
          saveJob({ ...next, status: 'cancelled' })
          return
        }
        const slice = next.historyIds.slice(next.nextIndex, next.nextIndex + batchSize)
        const result = await request(`${API}/batch`, {
          method: 'POST',
          secret: auth,
          body: { historyIds: slice, target: next.target }
        })
        const nextIndex = next.nextIndex + slice.length
        const missingHistoryIds = [...next.missingHistoryIds, ...result.missingHistoryIds]
        const missingGeometryIds = [...next.missingGeometryIds, ...result.missingGeometryIds]
        next = saveJob({
          ...next,
          nextIndex,
          missingHistoryIds,
          missingGeometryIds,
          progress: {
            batchNum: Math.ceil(nextIndex / batchSize),
            totalBatches: next.progress.totalBatches,
            totalInserted: next.progress.totalInserted + result.inserted,
            missingHistory: missingHistoryIds.length,
            missingGeometry: missingGeometryIds.length
          }
        })
      }

      await request(`${API}/complete`, {
        method: 'POST',
        secret: auth,
        body: { target: next.target }
      })
      saveJob({
        ...next,
        status: 'done',
        report: {
          totalRequested: next.totalRequested,
          totalInserted: next.progress.totalInserted,
          missingHistoryIds: next.missingHistoryIds,
          missingGeometryIds: next.missingGeometryIds
        }
      })
    } catch (err) {
      saveJob({ ...next, status: 'failed', error: err.message })
      setError(err.message)
    } finally {
      setBusy(false)
    }
  }

  const push = async (target) => {
    setError('')
    if (!secret) {
      setError('Enter the sync secret first.')
      return
    }
    rememberAuth()
    setBusy(true)
    try {
      const started = await request(`${API}/start`, {
        method: 'POST',
        secret,
        body: { historyIds: ids, target }
      })
      const next = saveJob({
        id: (typeof crypto !== 'undefined' && crypto.randomUUID)
          ? crypto.randomUUID()
          : `job-${Date.now()}`,
        status: 'running',
        target,
        historyIds: started.historyIds,
        nextIndex: 0,
        batchSize: started.batchSize,
        totalRequested: started.totalRequested,
        progress: {
          batchNum: 0,
          totalBatches: started.totalBatches,
          totalInserted: 0,
          missingHistory: 0,
          missingGeometry: 0
        },
        missingHistoryIds: [],
        missingGeometryIds: [],
        error: null,
        report: null
      })
      await runLoop(next, secret)
    } catch (err) {
      setError(err.message)
      setBusy(false)
    }
  }

  const resume = async () => {
    setError('')
    if (!secret) {
      setError('Enter the sync secret first.')
      return
    }
    rememberAuth()
    const next = saveJob({ ...job, status: 'running', error: null })
    await runLoop(next, secret)
  }

  const cancel = () => {
    cancelledRef.current = true
  }

  const openLiveConfirm = (action) => {
    setLiveAction(action)
    setLiveConfirmOpen(true)
  }

  const pushCountryProfiles = async (target) => {
    setError('')
    if (!secret) {
      setError('Enter the sync secret first.')
      return
    }
    rememberAuth()
    setBusy(true)
    const job = {
      status: 'running',
      target,
      datasets: [],
      current: null,
      done: 0,
      total: 0,
      rows: 0,
      error: null
    }
    setCountryJob(job)
    try {
      const started = await request(`${COUNTRY_API}/start`, {
        method: 'POST',
        secret,
        body: { target }
      })
      const views = started.views || []
      const datasets = []
      let rows = 0
      for (let i = 0; i < views.length; i += 1) {
        setCountryJob({
          ...job,
          datasets: [...datasets],
          current: views[i],
          done: i,
          total: views.length,
          rows
        })
        const result = await request(`${COUNTRY_API}/batch`, {
          method: 'POST',
          secret,
          body: { target, dataset: views[i] }
        })
        datasets.push(result)
        rows += result.rows || 0
      }
      await request(`${COUNTRY_API}/complete`, {
        method: 'POST',
        secret,
        body: { target }
      })
      setCountryJob({
        ...job,
        status: 'done',
        datasets,
        current: null,
        done: views.length,
        total: views.length,
        rows
      })
    } catch (err) {
      setCountryJob((current) => ({
        ...(current || job),
        status: 'failed',
        error: err.message
      }))
    } finally {
      setBusy(false)
    }
  }

  const onFile = async (event) => {
    const file = event.target.files?.[0]
    if (!file) return
    setFileName(file.name)
    setText(await file.text())
  }

  const progress = job?.progress
  const report = job?.report

  const liveCopy = liveAction === 'country'
    ? 'Are you sure you are pushing country profiles to live? This replaces the entire country profile dataset.'
    : 'Are you sure you are pushing factsheets data to live? This replaces the entire public factsheet set.'

  return (
    <Page>
      <Title>Sync factsheets</Title>
      <TabBar>
        <TabButton $active={tab === 'factsheets'} onClick={() => setTab('factsheets')} type='button'>
          Factsheets
        </TabButton>
        <TabButton $active={tab === 'countries'} onClick={() => setTab('countries')} type='button'>
          Country profiles
        </TabButton>
      </TabBar>
      {tab === 'factsheets' && (
      <Intro>
        The list you push becomes the entire public factsheet set on kba-website.
        Start truncates existing sites. Prefer staging first. Closing the tab pauses
        the push; use Resume last job to continue without truncating again.
      </Intro>
      )}
      {tab === 'countries' && (
      <Intro>
        Every public view named webcountry_* is read from WDKBA and replaces the country
        profile rows on kba-website.
      </Intro>
      )}

      <Label htmlFor='input-sync-secret'>Sync secret</Label>
      <InputField
        autoComplete='off'
        height='48px'
        id='input-sync-secret'
        onChange={(e) => setSecret(e.target.value)}
        type='password'
        value={secret}
      />

      {tab === 'factsheets' && (
      <>
      <FieldLabel htmlFor='input-history-ids' style={{ marginTop: 32 }}>
        History IDs
      </FieldLabel>
      <FileRow>
        <input
          accept='.txt,.csv'
          hidden
          onChange={onFile}
          ref={fileRef}
          type='file'
        />
        <Button height='40px' marginTop='0px' onClick={() => fileRef.current?.click()}>
          Upload txt / csv
        </Button>
        <FileName>{fileName || 'No file chosen'}</FileName>
      </FileRow>
      <TextArea
        id='input-history-ids'
        onChange={(e) => setText(e.target.value)}
        placeholder='One history id per line'
        value={text}
      />
      <Hint>
        {ids.length} id{ids.length === 1 ? '' : 's'} to push
      </Hint>

      <Actions>
        <Button
          height='40px'
          isDisabled={!canPush}
          marginTop='16px'
          onClick={() => push('staging')}
          type={canPush ? 'secondary' : 'defaultDisabled'}
        >
          Push to staging
        </Button>
        <Button
          height='40px'
          isDisabled={!canPush}
          marginTop='16px'
          onClick={() => openLiveConfirm('factsheets')}
          type={canPush ? 'secondary' : 'defaultDisabled'}
        >
          Push to live
        </Button>
        <Button
          height='40px'
          isDisabled={!resumeAvailable}
          marginTop='16px'
          onClick={resume}
          type={resumeAvailable ? 'secondary' : 'defaultDisabled'}
        >
          Resume last job
        </Button>
        <Button
          height='40px'
          isDisabled={!busy}
          marginTop='16px'
          onClick={cancel}
          type={busy ? 'secondary' : 'defaultDisabled'}
        >
          Cancel job
        </Button>
      </Actions>
      </>
      )}

      {tab === 'countries' && (
      <Actions>
        <Button
          height='40px'
          isDisabled={busy}
          marginTop='16px'
          onClick={() => pushCountryProfiles('staging')}
          type={busy ? 'defaultDisabled' : 'secondary'}
        >
          Push to staging
        </Button>
        <Button
          height='40px'
          isDisabled={busy}
          marginTop='16px'
          onClick={() => openLiveConfirm('country')}
          type={busy ? 'defaultDisabled' : 'secondary'}
        >
          Push to live
        </Button>
      </Actions>
      )}

      <ReactModal
        isOpen={liveConfirmOpen}
        onRequestClose={() => setLiveConfirmOpen(false)}
        style={liveModalStyles}
      >
        <div style={{ width: 416, position: 'relative' }}>
          <button
            onClick={() => setLiveConfirmOpen(false)}
            style={{
              position: 'absolute',
              top: 16,
              right: 16,
              border: 'none',
              background: 'transparent',
              cursor: 'pointer'
            }}
            type='button'
          >
            <CloseIcon />
          </button>
          <div style={{ padding: '32px 48px 0 32px', fontWeight: 600, fontSize: 18, lineHeight: '24px' }}>
            Push to live
          </div>
          <div style={{ padding: 32 }}>
            <ModalText>
              {liveCopy}
            </ModalText>
            <ModalFooter>
              <Button
                height='40px'
                marginTop='0px'
                onClick={() => setLiveConfirmOpen(false)}
                type='secondary'
              >
                Cancel
              </Button>
              <Button
                height='40px'
                marginTop='0px'
                onClick={() => {
                  setLiveConfirmOpen(false)
                  if (liveAction === 'country') pushCountryProfiles('live')
                  else push('live')
                }}
                type='secondary'
              >
                Push to live
              </Button>
            </ModalFooter>
          </div>
        </div>
      </ReactModal>

      {error && <ErrorText>{error}</ErrorText>}

      {tab === 'countries' && countryJob && (
        <ReportBox>
          <p>
            Country profiles — <strong>{countryJob.status}</strong>
            {countryJob.target ? ` (${countryJob.target})` : ''}
          </p>
          {countryJob.total > 0 && (
            <p>
              View {Math.min(countryJob.done + (countryJob.current ? 1 : 0), countryJob.total)}/{countryJob.total}
              {countryJob.current ? ` · ${countryJob.current}` : ''}
              {` · ${countryJob.rows} rows`}
            </p>
          )}
          {countryJob.error && <ErrorText>{countryJob.error}</ErrorText>}
          {countryJob.datasets.length > 0 && (
            <ReportPre>
              {countryJob.datasets.map((dataset) => `${dataset.name}: ${dataset.rows}`).join('\n')}
            </ReportPre>
          )}
        </ReportBox>
      )}

      {tab === 'factsheets' && job && (
        <ReportBox>
          <p>
            Job {job.id} — <strong>{job.status}</strong>
            {job.target ? ` (${job.target})` : ''}
          </p>
          {progress && (
            <p>
              Batch {progress.batchNum}/{progress.totalBatches} · inserted {progress.totalInserted} ·
              no history {progress.missingHistory} · no geometry {progress.missingGeometry}
            </p>
          )}
          {job.error && <ErrorText>{job.error}</ErrorText>}
          {report && (
            <ReportPre>
              {`Requested: ${report.totalRequested}
Inserted: ${report.totalInserted}
Missing history: ${report.missingHistoryIds?.length || 0}
Missing geometry: ${report.missingGeometryIds?.length || 0}`}
              {report.missingGeometryIds?.length
                ? `\n\nNo geometry:\n${report.missingGeometryIds
                    .slice(0, 50)
                    .map((row) => `  ${row.historyId}  ${row.siteCode}  ${row.name}`)
                    .join('\n')}`
                : ''}
            </ReportPre>
          )}
        </ReportBox>
      )}
    </Page>
  )
}

export default SyncFactsheetsPage
